import { isEqual } from 'lodash';
import { uniq } from 'lodash/fp';

import { validateActionStep } from 'dashboard/lib/workflows/validation/validateActionStep';
import { validateConditionalStep } from 'dashboard/lib/workflows/validation/validateConditionalStep';
import { validateGenericActionStep } from 'dashboard/lib/workflows/validation/validateGenericActionStep';
import { validateInputStep } from 'dashboard/lib/workflows/validation/validateInputStep';
import { validateLoopStep } from 'dashboard/lib/workflows/validation/validateLoopStep';
import { validateModuleStep } from 'dashboard/lib/workflows/validation/validateModuleStep';
import { validateOnError } from 'dashboard/lib/workflows/validation/validateOnError';
import { validateOutputs } from 'dashboard/lib/workflows/validation/validateOutputs';
import { validateParallelStep } from 'dashboard/lib/workflows/validation/validateParallelStep';
import { validateWaitStep } from 'dashboard/lib/workflows/validation/validateWaitStep';
import { WorkflowValidationContext } from 'dashboard/lib/workflows/validation/validationContext';
import { ExternalIntegrationVendor } from 'dashboard/models/external-integration-vendor';
import { WorkflowConfigError, WorkflowErrorType } from 'dashboard/models/workflow';
import { BlockInput } from 'dashboard/models/workflow-block';
import { GenericWorkflowAction } from 'dashboard/models/workflow-schema';
import {
  FakeWorkflowStepType,
  HttpPlatformIntegrationConfig,
  INPUT_STEP_TOKEN,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepType,
  WorkflowVersionOutput,
} from 'dashboard/models/workflow-version';
import { assertNever } from 'lib/utils/types';

export { errorFieldMatchesConditionalBranchIndex } from 'dashboard/lib/workflows/validation/validateConditionalStep';

export const validateWorkflowConfiguration = ({
  validationContext,
  outputs,
  timeOutMinutes,
  moduleBlockInputsByBlockId = {},
  externalIntegrationVendors,
}: {
  validationContext: WorkflowValidationContext;
  outputs: WorkflowVersionOutput[];
  externalIntegrationVendors: ExternalIntegrationVendor[];
  timeOutMinutes?: number;
  moduleBlockInputsByBlockId?: Record<string, BlockInput[]>;
}): WorkflowConfigError[] => {
  const stepsConfigErrors = validateStepsConfig({
    validationContext,
    moduleBlockInputsByBlockId,
    externalIntegrationVendors,
  });
  const inputStepErrors = validateInputStep(validationContext);
  const outputErrors = validateOutputs(outputs, validationContext);
  return [
    ...stepsConfigErrors,
    ...inputStepErrors,
    ...outputErrors,
    ...validateTimeout(timeOutMinutes),
  ];
};

const genericActionFrom = (
  step: WorkflowStepAction,
  validationContext: WorkflowValidationContext,
): GenericWorkflowAction | null =>
  validationContext.workflowSchema['generic-workflow-actions'].find((action) => {
    if (
      action.key === WorkflowStepActionKey.RunHttpPlatformIntegration &&
      step.action?.key === WorkflowStepActionKey.RunHttpPlatformIntegration
    ) {
      return isEqual(
        (action as HttpPlatformIntegrationConfig).integration,
        step.action.params.integration,
      );
    } else {
      return action.enabled && action.key === step.action?.key;
    }
  }) ?? null;

const validateStepsConfig = ({
  validationContext,
  moduleBlockInputsByBlockId,
  externalIntegrationVendors,
}: {
  validationContext: WorkflowValidationContext;
  moduleBlockInputsByBlockId: Record<string, BlockInput[]>;
  externalIntegrationVendors: ExternalIntegrationVendor[];
}): WorkflowConfigError[] => {
  const { graph } = validationContext;
  const errors: WorkflowConfigError[] = Object.values(graph).flatMap(({ step }) => {
    switch (step.type) {
      case WorkflowStepType.Placeholder:
        return {
          token: step.token,
          field: 'type',
          message: 'Step type should be selected',
          type: WorkflowErrorType.Save,
        };
      case WorkflowStepType.Action: {
        const genericAction = genericActionFrom(step, validationContext);
        return genericAction
          ? validateGenericActionStep(
              step,
              genericAction,
              validationContext,
              externalIntegrationVendors,
            )
          : validateActionStep(step, validationContext);
      }
      case WorkflowStepType.Conditional:
        return validateConditionalStep(step, validationContext);
      case WorkflowStepType.Loop:
        return validateLoopStep(step, validationContext);
      case WorkflowStepType.Module:
        return validateModuleStep(
          step,
          validationContext,
          moduleBlockInputsByBlockId[step['child-workflow-block-id']],
        );
      case WorkflowStepType.Parallel:
        return validateParallelStep(step);
      case WorkflowStepType.Wait:
        return validateWaitStep(step, validationContext);
      case FakeWorkflowStepType.MapOutput:
        return [];
      default:
        assertNever(step);
    }
  });

  const parents = (token: string): string[] =>
    graph[token].parent
      ? [graph[token].parent.step.token, ...parents(graph[token].parent.step.token)]
      : [];

  const parentErrors = uniq(
    errors.flatMap((error) => parents(error.token)),
  ).map<WorkflowConfigError>((token) => ({
    token,
    message: 'Nested step has error',
    field: graph[token].step.type === WorkflowStepType.Loop ? 'branch' : 'branches',
    type: WorkflowErrorType.Publish,
  }));

  const onErrorActionErrors = validateOnError(graph);

  return [...errors, ...parentErrors, ...onErrorActionErrors];
};

const validateTimeout = (timeout?: number): WorkflowConfigError[] => {
  if (timeout == null) {
    return [];
  }

  if (timeout < 0) {
    return [
      {
        token: INPUT_STEP_TOKEN,
        message: 'Timeout must be non-negative',
        type: WorkflowErrorType.Save,
      },
    ];
  }

  if (timeout > 525600) {
    return [
      {
        token: INPUT_STEP_TOKEN,
        message: 'Timeout must be less or equal to 525600 minutes (1 year)',
        type: WorkflowErrorType.Save,
      },
    ];
  }
  return [];
};
